import React, { FormEvent, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import GroupIcon from '@mui/icons-material/Group';

import {
  useAddJobHiringTeamMemberMutation,
  useGetJobHiringTeamQuery,
  useRemoveJobHiringTeamMemberMutation,
  useResendJobHiringTeamInviteMutation,
} from '../../services/serviceJobsAccount';

interface IHiringTeamPanelProps {
  eguid: string;
  compact?: boolean;
  listingTitle?: string;
}

export const HiringTeamPanel = ({
  eguid,
  compact = false,
  listingTitle,
}: IHiringTeamPanelProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const team = useGetJobHiringTeamQuery(eguid, { skip: !open || !eguid });
  const [addMember, addResult] = useAddJobHiringTeamMemberMutation();
  const [removeMember, removeResult] = useRemoveJobHiringTeamMemberMutation();
  const [resendInvite, resendResult] = useResendJobHiringTeamInviteMutation();
  const isWorking = addResult.isLoading || removeResult.isLoading || resendResult.isLoading;

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim();
    if (!normalized) return;
    try {
      await addMember({ eguid, email: normalized }).unwrap();
      setEmail('');
    } catch {
      // The shared error message below remains visible for retry.
    }
  };

  return (
    <>
      {compact ? (
        <Tooltip title="Manage hiring team">
          <IconButton
            size="small"
            aria-label={`Manage hiring team${listingTitle ? ` for ${listingTitle}` : ''}`}
            onClick={() => setOpen(true)}
          >
            <GroupIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button variant="outlined" startIcon={<GroupIcon />} onClick={() => setOpen(true)}>
          Hiring team
        </Button>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Hiring team</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Candidate access lets teammates review applicants for this job only, without managing the listing or billing.
          </Typography>
          {team.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              We could not load the hiring team. Close this dialog and try again.
            </Alert>
          ) : null}
          {addResult.error || removeResult.error || resendResult.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              We could not update the hiring team. Check the address or your access and try again.
            </Alert>
          ) : null}
          {addResult.isSuccess && addResult.data?.role === 'owner' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              That person already owns this listing. No candidate-access grant or email was added.
            </Alert>
          ) : null}
          {addResult.isSuccess && addResult.data?.role === 'candidateAccess' ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Access added. {addResult.data.inviteEmailState === 'queued' ? 'Email queued.' : 'Email needs retry.'}
            </Alert>
          ) : null}
          {team.isLoading || team.isFetching ? (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress size={22} aria-label="Loading hiring team" />
              <Typography color="text.secondary">Loading hiring team…</Typography>
            </Stack>
          ) : null}
          {team.data?.members.map((member, index) => (
            <Box key={`${member.role}-${member.userId}`}>
              {index > 0 ? <Divider /> : null}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ py: 1.5 }}
              >
                <Box>
                  <Typography fontWeight={800}>{member.name || member.email}</Typography>
                  <Typography color="text.secondary" variant="body2">{member.email}</Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.8, mt: 0.8 }}>
                    <Chip size="small" label={member.role === 'owner' ? 'Owner' : 'Candidate access'} />
                    <Chip
                      size="small"
                      color={member.accountState === 'active' ? 'success' : 'warning'}
                      label={member.accountState === 'active' ? 'Active' : 'Setup pending'}
                    />
                    {member.inviteEmailState === 'queued' ? <Chip size="small" label="Email queued" /> : null}
                    {member.inviteEmailState === 'needsRetry' ? <Chip size="small" color="warning" label="Email needs retry" /> : null}
                  </Stack>
                </Box>
                {member.role === 'candidateAccess' && team.data?.canManageTeam ? (
                  <Stack direction="row" spacing={1}>
                    {member.canResend ? (
                      <Button
                        size="small"
                        disabled={isWorking}
                        onClick={() => resendInvite({ eguid, userId: member.userId })}
                      >
                        {member.inviteEmailState === 'needsRetry' ? 'Retry email' : 'Resend invite'}
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      color="error"
                      disabled={isWorking}
                      onClick={() => removeMember({ eguid, userId: member.userId })}
                    >
                      Revoke
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          ))}
          {team.data?.canManageTeam ? (
            <Stack
              component="form"
              onSubmit={invite}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'flex-start' }}
              spacing={1}
              sx={{ mt: 2 }}
            >
              <TextField
                type="email"
                required
                label="Teammate email"
                helperText="We’ll email an invitation. New users will be prompted to finish account setup before they can review applicants for this job."
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                size="small"
                fullWidth
              />
              <Button type="submit" variant="contained" disabled={isWorking}>Invite</Button>
            </Stack>
          ) : team.data ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Only a listing owner can invite or remove teammates.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HiringTeamPanel;
